import React from 'react'
import { Box, Typography, Card, Grid, Chip } from '@mui/material'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import { Claim as ClaimData, ClaimStatus } from 'api/claims'
import { formatToTitleCase } from 'utils/formatUtils'

interface ClaimCardProps {
  claim: ClaimData
  onClick: (claimId: string) => void
}

const formatDate = (dateString: string): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }
  return new Date(dateString).toLocaleDateString(undefined, options)
}

const getStatusColor = (
  status: ClaimStatus
):
  | 'default'
  | 'primary'
  | 'secondary'
  | 'error'
  | 'info'
  | 'success'
  | 'warning' => {
  switch (status) {
    case ClaimStatus.PENDING:
      return 'info'
    case ClaimStatus.UNDER_REVIEW:
      return 'warning'
    case ClaimStatus.APPROVED:
      return 'success'
    case ClaimStatus.REJECTED:
      return 'error'
    case ClaimStatus.COMPLETED:
      return 'default'
    default:
      return 'default'
  }
}

const ClaimCard: React.FC<ClaimCardProps> = ({ claim, onClick }) => {
  const itemCount = claim.ClaimItems.length

  return (
    <Card
      sx={{
        mb: 3,
        p: 3,
        borderRadius: 3,
        position: 'relative',
        cursor: 'pointer',
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        border: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
        '&:hover': {
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          transform: 'translateY(-4px)',
          '& .arrow-icon': {
            transform: 'translateX(4px)',
            color: 'primary.main'
          }
        }
      }}
      onClick={() => onClick(claim.id)}
    >
      <Grid container spacing={2} alignItems='center'>
        <Grid item xs={12} sm={5}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Box
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                borderRadius: 2,
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <ReceiptLongIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography
                variant='h6'
                sx={{
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  color: 'text.primary'
                }}
              >
                #{claim.Order.netsuiteSalesOrderId}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mt: 0.5
                }}
              >
                <CalendarTodayIcon
                  sx={{ fontSize: 14, color: 'text.secondary' }}
                />
                <Typography variant='caption' color='text.secondary'>
                  {formatDate(claim.createdAt)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5
            }}
          >
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{ fontSize: '0.85rem' }}
            >
              {itemCount} Item{itemCount > 1 ? 's' : ''}
            </Typography>
          </Box>
        </Grid>

        <Grid
          item
          xs={12}
          sm={3}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
            gap: 2
          }}
        >
          <Chip
            label={formatToTitleCase(claim.status)}
            color={getStatusColor(claim.status)}
            sx={{
              fontWeight: 600,
              fontSize: '0.8rem',
              height: 32,
              px: 1
            }}
          />
          <ArrowForwardIosIcon
            className='arrow-icon'
            sx={{
              color: 'text.secondary',
              fontSize: 20,
              transition: 'all 0.3s'
            }}
          />
        </Grid>
      </Grid>
    </Card>
  )
}

export default ClaimCard
